import * as io from 'io-ts';
import { getApiUrl } from 'sdi/app';
import {
    deleteIO,
    fetchIO,
    PointIO,
    postIO,
    putIO,
    FeatureIO,
    FeatureCollectionIO,
} from 'sdi/source';
import { findIssue, OsmResourceId } from '../queries/qa';
import { fromEither, fromNullable } from 'fp-ts/lib/Option';
import { index } from 'fp-ts/lib/Array';
import { fst, Pair, scopeOption, snd } from 'sdi/lib';
import { u } from 'sdi/source/io/io';

const nullable = <A, O = A, I = unknown>(t: io.Type<A, O, I>) =>
    io.union([t, io.null]);

export const IssueStatusIO = io.union(
    [
        io.literal('RAISED'),
        io.literal('STALLED'),
        io.literal('FALSE_POSITIVE'),
        io.literal('CORRECTED'),
    ],
    'IssueStatusIO'
);
export type IssueStatus = io.TypeOf<typeof IssueStatusIO>;
const NullableIssueStatusIO = nullable(IssueStatusIO);
export type NullableIssueStatus = io.Type<typeof NullableIssueStatusIO>;

export const IssueOriginIO = io.union(
    [
        io.literal('QA'),
        io.literal('CHANGE_DETECTION_CREATION'),
        io.literal('CHANGE_DETECTION_MODIFICATION'),
        io.literal('CHANGE_DETECTION_DELETE'),
        io.literal('CREATION_OR_MODIFICATION'),
        // io.literal('COMPARISON'),
        // io.literal('ROUTING'),
        // io.literal('EXTERNAL'),
    ],
    'IssueOriginIO'
);
export type IssueOrigin = io.TypeOf<typeof IssueOriginIO>;
const NullableIssueOriginIO = nullable(IssueOriginIO);
export type NullableIssueOrigin = io.Type<typeof NullableIssueOriginIO>;

export const IssueCriticalityIO = io.union(
    [io.literal('high'), io.literal('medium'), io.literal('low')],
    'IssuePriorityIO'
);
export type IssueCriticality = io.TypeOf<typeof IssueCriticalityIO>;

const NullableIssueCriticalityIO = nullable(IssueCriticalityIO);
export type NullableIssueCriticality = io.Type<
    typeof NullableIssueCriticalityIO
>;

const OsmTypeIO = io.union([
    io.literal('node'),
    io.literal('way'),
    io.literal('relation'),
    // io.nullType
]);
export type OsmType = io.TypeOf<typeof OsmTypeIO>;

const NullableOsmTypeIO = nullable(OsmTypeIO);
export type NullableOsmType = io.TypeOf<typeof NullableOsmTypeIO>;

export const EvaluationIO = io.union([
    io.literal('WARNING'),
    io.literal('CONFIDENT'),
    io.literal('NONE'),
]);
export type Evaluation = io.TypeOf<typeof EvaluationIO>;

const NullableEvaluationIO = nullable(EvaluationIO);
export type NullableEvaluation = io.TypeOf<typeof NullableEvaluationIO>;

const nullableStringIO = nullable(io.string);
export type NullableString = io.TypeOf<typeof nullableStringIO>;

const nullableNumberIO = nullable(io.number);
export type NullableNumber = io.TypeOf<typeof nullableNumberIO>;

export const GlobalNoteIO = io.interface({
    id: io.number,
    text: io.string,
});
export type GlobalNote = io.TypeOf<typeof GlobalNoteIO>;

export const IssueNoteIO = io.intersection([
    GlobalNoteIO,
    io.interface({ issue_id: nullableNumberIO }),
]);
export type IssueNote = io.TypeOf<typeof IssueNoteIO>;

const NullableIssueNoteIO = nullable(EvaluationIO);
export type NullableIssueNote = io.TypeOf<typeof NullableIssueNoteIO>;

const IssueIO = io.interface(
    {
        id: io.number,
        timestamp: io.string,
        issue_type: io.number,
        status: NullableIssueStatusIO,
        osm_id: nullableNumberIO,
        osm_type: NullableOsmTypeIO,
        osm_version: nullableNumberIO,
        osm_user: nullableStringIO,
        osm_timestamp: nullableStringIO,
        criticity: NullableIssueCriticalityIO,
        origin: NullableIssueOriginIO,
        changeset_id: nullableNumberIO,
        point: PointIO,
        // linked_issues: io.array(io.number),
        level: io.array(io.number),
    },
    'IssueIO'
);
export type Issue = io.TypeOf<typeof IssueIO>;

const ExternalIssueIO = io.interface(
    {
        issue: io.number,
        external_id: io.number,
        temporary_closed: io.boolean,
        definitely_closed: io.boolean,
        wheelchairaccessible: io.boolean,
        is_not_present: io.boolean,
        creation_date: io.string,
        comment: io.string,
        new_name: io.string,
    },
    'ExternalIssueIO'
);
export type ExternalIssue = io.TypeOf<typeof ExternalIssueIO>;

export const IssueTypeOsmObjectIO = io.union(
    [
        // io.literal('vending-machine'),
        // io.literal('information-panel'),
        // io.literal('steps'),
        // io.literal('foot-routing'),
        // io.literal('lift'),
        // io.literal('exit'),
        // io.literal('emergency'),
        // io.literal('restaurant'),
        // io.literal('toilet'),
        // io.literal('taxi'),
        // io.literal('barrier'),
        // io.literal('service'),
        // io.literal('intermodality'),
        // io.literal('street-furniture'),
        // io.literal('shop'),
        // io.literal('phone'),
        io.literal('service'),
        io.literal('structure'),
        io.literal('intermodality'),
        io.literal('parking'),
        io.literal('bicycle'),
        io.literal('energy'),
    ],
    'IssueTypeOsmObjectIO'
);

export type IssueTypeOsmObject = io.TypeOf<typeof IssueTypeOsmObjectIO>;

const IssueTypeIO = io.interface(
    {
        id: io.number,
        short_description: io.string,
        description: io.string,
        origin: NullableIssueOriginIO,
        osm_object: nullable(IssueTypeOsmObjectIO),
        criticity: NullableIssueCriticalityIO,
    },
    'IssueTypeIO'
);
export type IssueType = io.TypeOf<typeof IssueTypeIO>;

const LinkedIssuesLinkIO = io.union(
    [
        io.literal('OSM_OBJECT'),
        io.literal('CHANGESET'),
        io.literal('LOCATION'),
        io.literal('MANUAL'),
    ],
    'LinkedIssuesLinkIO'
);
export type LinkedIssuesLink = io.TypeOf<typeof LinkedIssuesLinkIO>;

const LinkedIssuesIO = io.interface(
    {
        id: io.number,
        link: LinkedIssuesLinkIO,
        issues: io.array(io.number),
    },
    'LinkedIssuesIO'
);
export type LinkedIssues = io.TypeOf<typeof LinkedIssuesIO>;

const EvaluationListIO = io.interface(
    {
        user_uid: io.number,
        user_name: io.string,
        evaluation: NullableEvaluationIO,
    },
    'EvaluationListIO'
);
export type EvaluationList = io.TypeOf<typeof EvaluationListIO>;

const OsmUserIO = io.interface({
    id: io.number,
    name: io.string,
});
export type OsmUser = io.TypeOf<typeof OsmUserIO>;

const ResolvingChangesetIO = io.interface({
    id: io.number,
    user: io.string,
    uid: io.number,
    issues: io.array(io.number),
});
export type ResolvingChangeset = io.TypeOf<typeof ResolvingChangesetIO>;

const OsmConnexionIO = io.interface({
    uid: io.string,
    extras: io.interface({
        id: io.string,
        display_name: io.string,
        account_created: io.string,
    }),
});

const CurrentOsmUserIO = io.interface({
    anonymous: io.boolean,
    username: io.string,
    openstreetmap: nullable(OsmConnexionIO),
});
export type CurrentOsmUser = io.TypeOf<typeof CurrentOsmUserIO>;

export const fetchIssueList = () =>
    fetchIO(io.array(IssueIO), getApiUrl('geodata/qa/issues/'));

export const fetchIssueListByTheme = (theme: string) =>
    fetchIO(io.array(IssueIO), getApiUrl(`geodata/qa/issues/${theme}`));

export const fetchCurrentOsmUser = () =>
    fetchIO(CurrentOsmUserIO, getApiUrl(`geodata/qa/user/get_info/`));

export const putIssue = (id: number, i: Partial<Issue>) =>
    putIO(IssueIO, getApiUrl(`geodata/qa/issues/${id}/`), i);
export const putIssueNote = (id: number, n: IssueNote) =>
    putIO(IssueNoteIO, getApiUrl(`geodata/qa/notebook/issues/${id}/`), n);
export const putGlobalNote = (id: number, n: GlobalNote) =>
    putIO(GlobalNoteIO, getApiUrl(`geodata/qa/notebook/global/${id}/`), n);
export const putEvaluation = (id: number, evalList: EvaluationList) =>
    putIO(
        EvaluationListIO,
        getApiUrl(`geodata/qa/user-evaluations/${id}/`),
        evalList
    );
export const putLinkedIssues = (id: number, linkedIssues: LinkedIssues) =>
    putIO(
        LinkedIssuesIO,
        getApiUrl(`geodata/qa/linked-issues/${id}/`),
        linkedIssues
    );

export const postIssueNote = (n: Omit<IssueNote, 'id'>) =>
    postIO(IssueNoteIO, getApiUrl(`geodata/qa/notebook/issues/`), n);
export const postGlobalNote = (n: Omit<GlobalNote, 'id'>) =>
    postIO(GlobalNoteIO, getApiUrl(`geodata/qa/notebook/global/`), n);
export const postEvaluation = (evalList: EvaluationList) =>
    postIO(
        EvaluationListIO,
        getApiUrl(`geodata/qa/user-evaluations/`),
        evalList
    );
export const postLinkedIssues = (linkedIssues: Omit<LinkedIssues, 'id'>) =>
    postIO(
        LinkedIssuesIO,
        getApiUrl(`geodata/qa/linked-issues/`),
        linkedIssues
    );
export const postResolvingChangeset = (changeset: ResolvingChangeset) =>
    postIO(
        ResolvingChangesetIO,
        getApiUrl(`geodata/qa/resolving-changeset/`),
        changeset
    );

export const deleteLinkedIssues = (id: number) =>
    deleteIO(getApiUrl(`geodata/qa/linked-issues/${id}/`));
export const deleteIssueNote = (id: number) =>
    deleteIO(getApiUrl(`geodata/qa/notebook/issues/${id}/`));
export const deleteGlobalNote = (id: number) =>
    deleteIO(getApiUrl(`geodata/qa/notebook/global/${id}/`));

export const postTagList = (tagForm: TagForm) => {
    const issue = findIssue(tagForm.issueId);
    return scopeOption()
        .let(
            'osmType',
            issue.chain(i => fromNullable(i.osm_type))
        )
        .let(
            'osmId',
            issue.chain(i => fromNullable(i.osm_id))
        )
        .map(({ osmType, osmId }) => {
            const tagDict: TagDict = {};
            tagForm.tags.map(tag => {
                tagDict[fst(tag.pair)] = snd(tag.pair);
            });
            const tagChangeset = {
                tags: tagDict,
                comment: tagForm.comment,
                source: tagForm.source,
            };
            return postIO(
                TagChangesetIO,
                getApiUrl(`geodata/osm/update/${osmType}/${osmId}`),
                tagChangeset
            );
        });
};

export const fetchIssueTypes = () =>
    fetchIO(io.array(IssueTypeIO), getApiUrl('geodata/qa/issue-types'));

export const fetchLinkedIssues = (issueId: number) =>
    fetchIO(
        io.array(LinkedIssuesIO),
        getApiUrl(`geodata/qa/linked/${issueId}`)
    );

export const fetchEvaluationList = () =>
    fetchIO(
        io.array(EvaluationListIO),
        getApiUrl('geodata/qa/user-evaluations/')
    );

export const fetchIssueNoteList = () =>
    fetchIO(io.array(IssueNoteIO), getApiUrl('geodata/qa/notebook/issues/'));

export const fetchGlobalNoteList = () =>
    fetchIO(io.array(GlobalNoteIO), getApiUrl('geodata/qa/notebook/global/'));

export const fetchOsmUser = (userName: string) =>
    fetchIO(
        io.union([OsmUserIO, io.nullType]),
        getApiUrl(`geodata/qa/get_user_uid/${userName}`)
    );

export const fetchExternalIssue = (id: number) =>
    fetchIO(ExternalIssueIO, getApiUrl(`geodata/qa/ext-issues/${id}`));

const TagDictIO = io.dictionary(io.string, io.string, 'TagDictIO');
export type TagDict = io.TypeOf<typeof TagDictIO>;

const TagChangesetIO = io.interface({
    tags: TagDictIO,
    comment: io.string,
    source: io.string,
});
export type TagChangeset = io.TypeOf<typeof TagChangesetIO>;

const RelationMemberIO = io.interface({
    type: OsmTypeIO, // mostly "node"
    ref: io.number, // node id
    role: io.string,
});
export type RelationMember = io.TypeOf<typeof RelationMemberIO>;

const OsmElementBaseIO = io.intersection(
    [
        io.interface({
            id: io.number,
            timestamp: io.string,
            version: io.number,
            changeset: io.number,
            user: io.string,
            uid: io.number,
        }),
        io.partial({
            tags: TagDictIO,
        }),
    ],
    'OsmElementBaseIO'
);

export const OsmElementNodeIO = io.intersection(
    [
        OsmElementBaseIO,
        io.interface({
            type: io.literal('node'),
        }),
        io.partial({
            lat: io.number,
            lon: io.number,
        }),
    ],
    'OsmElementNodeIO'
);

export const OsmElementNodePosIO = io.intersection(
    [
        OsmElementBaseIO,
        io.interface({
            type: io.literal('node'),
            lat: io.number,
            lon: io.number,
        }),
    ],
    'OsmElementNodePosIO'
);

export const OsmElementWayIO = io.intersection(
    [
        OsmElementBaseIO,
        io.interface({
            type: io.literal('way'),
        }),
        io.partial({
            nodes: io.array(io.number),
        }),
    ],
    'OsmElementWayIO'
);

export const OsmElementRelationIO = io.intersection(
    [
        OsmElementBaseIO,
        io.interface({
            type: io.literal('relation'),
            members: io.array(RelationMemberIO),
        }),
    ],
    'OsmElementRelationIO'
);

const OsmElementIO = io.union(
    [OsmElementNodeIO, OsmElementWayIO, OsmElementRelationIO],
    'OsmElementIO'
);

export type OsmElementNode = io.TypeOf<typeof OsmElementNodeIO>;
export type OsmElementNodePos = io.TypeOf<typeof OsmElementNodePosIO>;
export type OsmElementWay = io.TypeOf<typeof OsmElementWayIO>;
export type OsmElementRelation = io.TypeOf<typeof OsmElementRelationIO>;
export type OsmElement = io.TypeOf<typeof OsmElementIO>;

export const posOsmElementNode = (n: OsmElementNode | OsmElementNodePos) =>
    fromEither(OsmElementNodePosIO.decode(n));

const OsmObjectIO = io.interface(
    {
        version: io.string,
        generator: io.string,
        copyright: io.string,
        attribution: io.string,
        license: io.string,
        elements: io.array(OsmElementIO),
    },
    'OsmObjectIO'
);
export type OsmObject = io.TypeOf<typeof OsmObjectIO>;

export const getElement = (obj: OsmObject) => index(0, obj.elements);

export const fetchOsmObject = ({ id, type, version }: OsmResourceId) =>
    fetchIO(
        OsmObjectIO,
        `https://api.openstreetmap.org/api/0.6/${type}/${id}/${version}.json`
    );

const OsmRelationObjectIO = new io.Type<OsmElementRelation, OsmElementRelation>(
    'OsmRelationObjectIO',
    (u): u is OsmElementRelation =>
        OsmObjectIO.decode(u).fold(
            () => false,
            obj =>
                obj.elements.length === 1 && obj.elements[0].type === 'relation'
        ),
    (u, ctx) =>
        OsmObjectIO.decode(u).chain(obj =>
            obj.elements.length === 1 && obj.elements[0].type === 'relation'
                ? io.success(obj.elements[0])
                : io.failure(u, ctx)
        ),
    obj => obj
);

const OsmWayObjectIO = new io.Type<OsmElementWay, OsmElementWay>(
    'OsmWayObjectIO',
    (u): u is OsmElementWay =>
        OsmObjectIO.decode(u).fold(
            () => false,
            obj => obj.elements.length === 1 && obj.elements[0].type === 'way'
        ),
    (u, ctx) =>
        OsmObjectIO.decode(u).chain(obj =>
            obj.elements.length === 1 && obj.elements[0].type === 'way'
                ? io.success(obj.elements[0])
                : io.failure(u, ctx)
        ),
    obj => obj
);

const OsmNodeObjectIO = new io.Type<OsmElementNode, OsmElementNode>(
    'OsmNodeObjectIO',
    (u): u is OsmElementNode =>
        OsmObjectIO.decode(u).fold(
            () => false,
            obj => obj.elements.length === 1 && obj.elements[0].type === 'node'
        ),
    (u, ctx) =>
        OsmObjectIO.decode(u).chain(obj =>
            obj.elements.length === 1 && obj.elements[0].type === 'node'
                ? io.success(obj.elements[0])
                : io.failure(u, ctx)
        ),
    obj => obj
);

const OsmNodeObjectListIO = new io.Type<OsmElementNode[], OsmElementNode[]>(
    'OsmNodeObjectListIO',
    (u): u is OsmElementNode[] =>
        OsmObjectIO.decode(u).fold(
            () => false,
            _obj => true
        ),
    (u, _ctx) =>
        OsmObjectIO.decode(u).map(obj =>
            obj.elements
                .filter(e => e.type === 'node')
                .map<OsmElementNode>(e => e as OsmElementNode)
        ),
    obj => obj
);

export type TagState = 'created' | 'modified' | 'deleted' | 'none';

export interface Tag {
    pair: Pair<string, string>;
    state: TagState;
}

export interface TagForm {
    issueId: number;
    tags: Tag[];
    comment: string;
    source: string;
}

export const fetchOsmVersionedNode = (id: number, version: number) =>
    fetchIO(
        OsmNodeObjectIO,
        `https://api.openstreetmap.org/api/0.6/node/${id}/${version}.json`
    );

export const fetchOsmVersionedWay = (id: number, version: number) =>
    fetchIO(
        OsmWayObjectIO,
        `https://api.openstreetmap.org/api/0.6/way/${id}/${version}.json`
    );

export const fetchOsmVersionedrelation = (id: number, version: number) =>
    fetchIO(
        OsmRelationObjectIO,
        `https://api.openstreetmap.org/api/0.6/relation/${id}/${version}.json`
    );

export const fetchOsmNode = (id: number) =>
    fetchIO(
        OsmNodeObjectIO,
        `https://api.openstreetmap.org/api/0.6/node/${id}.json`
    );

export const fetchOsmWay = (id: number) =>
    fetchIO(
        OsmWayObjectIO,
        `https://api.openstreetmap.org/api/0.6/way/${id}.json`
    );

export const fetchOsmRelation = (id: number) =>
    fetchIO(
        OsmRelationObjectIO,
        `https://api.openstreetmap.org/api/0.6/relation/${id}.json`
    );

export const fetchOsmNodeList = (ids: number[]) =>
    fetchIO(
        OsmNodeObjectListIO,
        `https://api.openstreetmap.org/api/0.6/nodes.json?nodes=${ids.join(
            ','
        )}`
    );

// tslint:disable-next-line: variable-name
export const OsmChangeIO = io.interface(
    {
        changeset: io.number,
        modify: io.array(OsmElementIO),
        create: io.array(OsmElementIO),
        delete: io.array(OsmElementIO),
    },
    'OsmChangeIO'
);

export type OsmChange = io.TypeOf<typeof OsmChangeIO>;

const IssueGeometryIO = io.interface(
    {
        current: nullable(FeatureIO),
        previous: nullable(FeatureIO),
    },
    'IssueGeometryIO'
);

export type IssueGeometry = io.TypeOf<typeof IssueGeometryIO>;

const IssueGeometryCollectionIO = io.interface(
    {
        current: u([FeatureCollectionIO, io.nullType, io.undefined]),
        previous: u([FeatureCollectionIO, io.nullType, io.undefined]),
    },
    'IssueGeometryIO'
);

export type IssueGeometryCollection = io.TypeOf<
    typeof IssueGeometryCollectionIO
>;

export const fetchIssueGeometry = (issueId: number) =>
    fetchIO(
        IssueGeometryIO,
        getApiUrl(`geodata/qa/issues/${issueId}/geometries/`)
    );

export const fetchIssueGeometryCollection = (issueId: number) =>
    fetchIO(
        IssueGeometryCollectionIO,
        getApiUrl(`geodata/qa/issues/${issueId}/geometries/`)
    );

const ThemeIO = io.interface(
    {
        id: io.number,
        name: io.string,
        description: io.string,
        slug: io.string,
        map_complete_theme: nullable(io.string),
        issue_type_id: io.array(io.number),
        image: nullable(io.string),
        created_at: io.string,
        updated_at: io.string,
        active: io.boolean,
    },
    'ThemeIO'
);
export type Theme = io.TypeOf<typeof ThemeIO>;

export const fetchThemes = () =>
    fetchIO(io.array(ThemeIO), getApiUrl('geodata/qa/themes/'));
